import cx from 'classnames'
import { type FC, type ReactNode, useReducer, useState } from 'react'

import IconArrowRightMini from '../../icons/IconArrowRightMini'
import { ResizeObservedWrapper } from '../ResizeObservedWrapper'
import css from './CollapsableBlock.module.scss'

type Props = {
  title: ReactNode
  control?: ReactNode
  withoutContentPadding?: boolean
  children: ReactNode
  className?: string
  upperUnderline?: 'full' | 'partial'
  animated?: boolean
  isOpenedOnStart?: boolean
}

const CollapsableBlock: FC<Props> = ({
  title,
  control,
  withoutContentPadding,
  children,
  className,
  upperUnderline,
  animated,
  isOpenedOnStart = false,
}) => {
  const [realHeight, setRealHeight] = useState<number | null>(null)
  const [opened, toggleOpened] = useReducer((state) => !state, isOpenedOnStart)

  const classes = {
    container: cx(css.container, className),
    header: cx(css.header, {
      [css.opened]: opened,
    }),
    arrowContainer: cx(css.arrowContainer, {
      [css.upperUnderline]: upperUnderline === 'full',
    }),
    arrow: css.arrow,
    title: cx(css.title, {
      [css.upperUnderline]:
        upperUnderline === 'partial' || upperUnderline === 'full',
    }),
    control: cx(css.control, {
      [css.upperUnderline]:
        upperUnderline === 'partial' || upperUnderline === 'full',
    }),
    contentOuter: css.contentOuter,
    contentInner: cx({ [css.contentInner]: !withoutContentPadding }),
  }

  const height = animated ? `${realHeight}px` : undefined
  const contentOuterStyle = opened
    ? ({
        height,
        opacity: '1',
        pointerEvents: 'auto',
      } as React.CSSProperties)
    : { height: 0 }

  return (
    <div className={classes.container}>
      <div className={classes.header} onClick={toggleOpened}>
        <div className={classes.arrowContainer}>
          <IconArrowRightMini className={classes.arrow} />
        </div>
        <div className={classes.title}>{title}</div>
        {control && <div className={classes.control}>{control}</div>}
      </div>
      <div className={classes.contentOuter} style={contentOuterStyle}>
        {!animated && <div className={classes.contentInner}>{children}</div>}
        {animated && (
          <ResizeObservedWrapper onResize={setRealHeight}>
            <div className={classes.contentInner}>{children}</div>
          </ResizeObservedWrapper>
        )}
      </div>
    </div>
  )
}

export default CollapsableBlock
